import {
  Alert,
  Box,
  Button,
  CssBaseline,
  FilledInput,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import AppBar from "./components/AppBar";
import { CustomCircularProgressFullScreen } from "../../components/CustomCircularProgress";
import { useEffect, useState } from "react";
import { MuiTelInput } from "mui-tel-input";
import AutoCompleteCustom from "../../components/AutoCompleteCustom";
import { Image, Save } from "@mui/icons-material";
import { useFormDialog } from "../../hooks/useFormDialog";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import ImageFormDialog from "./components/ImageFormDialog";
import { useOption } from "./hooks/useOption";
import { usePageTitle } from "../../components/PageTitleProvider";
import { useExternalService } from "../../service/external";
import { useNotificationSnackbar } from "../../hooks/useNotificationSnackbar";
import { useValidateString } from "../../utils/useValidateString";
import { TextMaskCustom } from "../../components/TextMaskCustom";
import html2canvas from "html2canvas";
import lojaImg from "../../utils/assets/loja.gif";
import { IExternalMcFish } from "../../service/external/interfaces/IExternalMcFish";

export interface McFishProps {
  colorMode: any;
}

interface Errors {
  documento: string | null;
  nome: string | null;
  email: string | null;
  telefone: string | null;
  dataPedido: string | null;
  loja: string | null;
  contato: string | null;
  cidadeEstado: string | null;
}

const McFish: React.FC<McFishProps> = ({ colorMode }) => {
  const { setPageTitle } = usePageTitle();
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    setPageTitle("Formulario de McFish");
  }, [setPageTitle]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const externalModel: IExternalMcFish = {
    documento: "",
    nome: "",
    email: "",
    pedido: "",
    telefone: "",
    dataPedido: dayjs().startOf("day"),
    loja: "",
    screenshot: null,
    contato: "",
    cidadeEstado: "",
  };

  const initError: Errors = {
    documento: null,
    nome: null,
    email: null,
    telefone: null,
    dataPedido: null,
    loja: null,
    contato: null,
    cidadeEstado: null,
  };
  const [model, setModel] = useState<IExternalMcFish>(externalModel);
  const [errors, setErrors] = useState<Errors>(initError);
  const [numberCase, setNumberCase] = useState<string>("");

  const [lojas, lojasLoading] = useOption("LO3");
  const [saveButton, setSaveButton] = useState<boolean>(false);

  useEffect(() => {
    if (!lojasLoading) {
      setLoading(false);
    }
  }, [lojasLoading]);

  const { saveFormMcFish } = useExternalService();
  const { onErrorSnackbar } = useNotificationSnackbar();

  const [
    isOpenImgFormDialog,
    titleImgFormDialog,
    openImgFormDialog,
    closeImgFormDialog,
  ] = useFormDialog();
  const [imgState, setImgState] = useState<any>();

  const clearError = () => {
    setErrors(initError);
  };
  const { validateEmail, validateTelefone } = useValidateString();

  const validFields = () => {
    const valid: boolean[] = [];

    if (!model.documento) {
      setErrors((prevState) => ({
        ...prevState,
        documento: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (!model.nome.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        nome: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }
    if (!model.email.trim() || !validateEmail(model.email)) {
      setErrors((prevState) => ({
        ...prevState,
        email: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    const telefoneValidation = validateTelefone(model.telefone);
    if (!telefoneValidation.valid) {
      setErrors((prevState) => ({
        ...prevState,
        telefone: telefoneValidation.message,
      }));
      valid.push(telefoneValidation.valid);
    }

    if (!model.cidadeEstado.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        loja: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (!model.loja.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        loja: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (model.dataPedido === null) {
      setErrors((prevState) => ({
        ...prevState,
        dataPedido: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    if (!model.dataPedido?.isValid()) {
      setErrors((prevState) => ({
        ...prevState,
        dataPedido: "❌ Data inválida",
      }));
      valid.push(false);
    }

    if (model.contato === null || !model.contato?.trim()) {
      setErrors((prevState) => ({
        ...prevState,
        contato: "❌ Campo obrigatório",
      }));
      valid.push(false);
    }

    return valid.length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    if (!validFields()) return;
    setSaveButton(true);
    const element = document.getElementById("Screenshot");
    if (element) {
      html2canvas(element).then((canvas) => {
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], "Formulario.png", {
              type: "image/png",
            });
            model.screenshot = file;
            setLoading(true);
            const response = await saveFormMcFish(model);
            if (response.status === 200) {
              setLoading(false);
              setSaveButton(false);
              setModel(externalModel);
              setNumberCase(response.data);
            } else {
              setLoading(false);
              setSaveButton(false);
              setModel(externalModel);
              onErrorSnackbar(response.data);
            }
          }
        });
      });
    } else {
      onErrorSnackbar("Screenshot");
      setSaveButton(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        overflowX: "hidden",
      }}
    >
      <CssBaseline />
      <AppBar />
      <Box
        sx={{
          flex: 1,
          mt: 8,
          pl: 2,
          pr: 2,
          pb: 2,
          overflowX: "hidden",
        }}
      >
        {loading && <CustomCircularProgressFullScreen />}
        {!numberCase ? (
          <Box
            component="form"
            id="Screenshot"
            noValidate
            onSubmit={handleSubmit}
          >
            <Grid container spacing={2}>
              <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
                <Stack
                  direction="row"
                  alignContent="center"
                  justifyContent="center"
                >
                  <Typography
                    variant={isSmallScreen ? "subtitle1" : "h5"}
                    fontWeight={700}
                  >
                    FORMULARIO DE SOLICITAÇÃO DE INFORMATIVO SOBRE
                    DISPONIBILIDADE DE MCFISH
                  </Typography>
                </Stack>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
                <Stack
                  direction="row"
                  alignContent="center"
                  justifyContent="start"
                >
                  <Typography variant={isSmallScreen ? "body2" : "body1"}>
                    Este formulário é destinado para clientes que tentaram
                    comprar o McFish e o restaurante estava sem o produto
                    disponível.
                  </Typography>
                </Stack>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
                <Stack
                  direction="row"
                  alignContent="center"
                  justifyContent="start"
                >
                  <Typography variant={isSmallScreen ? "body2" : "body1"}>
                    *Todos os campos do formulário são obrigatórios.
                  </Typography>
                </Stack>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <FormControl
                  variant="filled"
                  fullWidth
                  required
                  error={!!errors.documento}
                >
                  <InputLabel>CPF</InputLabel>
                  <FilledInput
                    value={model.documento}
                    onChange={(event) =>
                      setModel((prevState: any) => ({
                        ...prevState,
                        documento: event.target.value,
                      }))
                    }
                    inputComponent={TextMaskCustom as any}
                    inputProps={{
                      mask: "000.000.000-00", // Pass the mask as a prop to TextMaskCustom
                    }}
                  />
                  {!!errors.documento && (
                    <FormHelperText error={!!errors.documento}>
                      {errors.documento || ""}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="QUAL SEU NOME COMPLETO?"
                  variant="filled"
                  value={model.nome}
                  onChange={(event) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      nome: event.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.nome}
                  helperText={errors.nome || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="QUAL SEU E-MAIL PARA CONTATO?"
                  variant="filled"
                  value={model.email}
                  onChange={(event) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      email: event.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.email}
                  helperText={errors.email || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <MuiTelInput
                  label="QUAL SEU TELEFONE PARA CONTATO?"
                  variant="filled"
                  defaultCountry="BR"
                  value={model.telefone}
                  onChange={(value) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      telefone: value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.telefone}
                  helperText={errors.telefone || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <TextField
                  label="QUAL A SUA CIDADE E ESTADO (Exemplo: São Paulo – SP)?"
                  variant="filled"
                  value={model.cidadeEstado}
                  onChange={(event) =>
                    setModel((prevState: any) => ({
                      ...prevState,
                      cidadeEstado: event.target.value,
                    }))
                  }
                  fullWidth
                  required
                  error={!!errors.cidadeEstado}
                  helperText={errors.cidadeEstado || ""}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <Alert
                  severity="info"
                  sx={{ fontWeight: 700 }}
                  action={
                    <Button
                      startIcon={<Image />}
                      size="small"
                      onClick={() => {
                        setImgState(lojaImg);
                        openImgFormDialog(
                          "EM QUE LOJA TENTOU COMPRAR O MCFISH?"
                        );
                      }}
                    >
                      Exemplo
                    </Button>
                  }
                >
                  EM QUE LOJA TENTOU COMPRAR O MCFISH?
                </Alert>
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <AutoCompleteCustom
                  title="EM QUE LOJA TENTOU COMPRAR O MCFISH?"
                  options={lojas ?? []}
                  field="label"
                  fieldId="idBRAN"
                  value={model.loja}
                  onChange={(id) =>
                    setModel((prevState) => ({
                      ...prevState,
                      loja: id ?? "",
                    }))
                  }
                  variant="filled"
                  required
                  error={errors.loja}
                />
              </Grid>

              <Grid xs={12} sm={12} md={12} lg={12}>
                <DatePicker
                  label="QUAL A DATA QUE TENTOU COMPRAR?"
                  localeText={{
                    toolbarTitle: "QUAL A DATA QUE TENTOU COMPRAR?",
                    cancelButtonLabel: "CANCELAR",
                    okButtonLabel: "OK",
                  }}
                  value={model.dataPedido}
                  onChange={(newValue: any) => {
                    setModel((prevState) => ({
                      ...prevState,
                      dataPedido: newValue,
                    }));
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "filled",
                      error: !!errors.dataPedido,
                      helperText: errors.dataPedido || "",
                      required: true,
                    },
                  }}
                />
              </Grid>
              <Grid xs={12} sm={12} md={12} lg={12}>
                <FormControl fullWidth required error={!!errors.contato}>
                  <FormLabel>
                    GOSTARIA DE RECEBER UM CONTATO INFORMANDO SOBRE A
                    DISPONIBILIDADE DO MC FISH PRÓXIMO Á VOCÊ?
                  </FormLabel>
                  <RadioGroup
                    value={model.contato}
                    onChange={(event) =>
                      setModel((prevState: any) => ({
                        ...prevState,
                        contato: event.target.value,
                      }))
                    }
                    row
                  >
                    <FormControlLabel
                      value="Sim"
                      control={<Radio />}
                      label="Sim"
                    />
                    <FormControlLabel
                      value="Não"
                      control={<Radio />}
                      label="Não"
                    />
                  </RadioGroup>
                  {!!errors.contato && (
                    <FormHelperText error={!!errors.contato}>
                      {errors.contato || ""}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid xs={12} sm={12} md={12} lg={12}>
                <Button
                  startIcon={<Save />}
                  type="submit"
                  fullWidth
                  disabled={saveButton}
                >
                  Finalizar
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid xs={12} sm={12} md={12} lg={12}>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Typography variant="h3">😃</Typography>
                <Typography variant="subtitle1">
                  Obrigado!.
                </Typography>                
                <Typography variant="subtitle1">
                  Número do Caso:
                  <Typography component="span" fontWeight={700}>
                    {" "}
                    {numberCase}
                  </Typography>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        )}
        <ImageFormDialog
          isOpen={isOpenImgFormDialog}
          title={titleImgFormDialog}
          close={closeImgFormDialog}
          image={imgState}
        />
      </Box>
    </Box>
  );
};

export default McFish;
